import {
    DW_EVENT_CHANNEL_KEY,
    DW_RECIPIENT_ID_KEY,
} from '@/libs/analytics/data-warehouse/amplitude';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import useAppConfig from '@rhg/hooks/use-app-config';
import useDwAmplitude from '@rhg/hooks/use-dw-amplitude';
import createAnalyticsApi from '@rhg/libs/api/create-analytics-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { useMutation } from '@rhg/libs/react-query';
import { getRhgAppHeader, getRhgAppHeaderObject } from '@rhg/utils/browser';
import {
    getLocalDateTimeString,
    getUtcDateTimeString,
} from '@rhg/utils/date-time';

export interface DataWarehouseEventData {
    action: string;
    user_id: string;
    recipient_id: string | number;
    analytics_id: string;
    screen_name: string;
}
export interface ToolEventData {
    action: string;
    workbook_id: string;
    workbook_title: string;
    pack_class: string;
    program_id: string;
    program_title: string;
    program_is_clinical: boolean;
}

interface TrackDataWarehouseEventType {
    requestData: DataWarehouseEventData | ToolEventData;
    eventName?: string;
}

export default function useAnalyticsStreamEvents() {
    const { activeCircle } = useActiveCircle();
    const { getAccessTokenSilently } = useAuth();
    const { trackDwEvent, isEnabled: isDwAmplitudeEnabled } = useDwAmplitude();
    const { NEXT_PUBLIC_FF_DW_STREAMING_ENABLED: dwStreamingEnabled } =
        useAppConfig();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            requestData,
            eventName = 'app_events',
        }: TrackDataWarehouseEventType) => {
            if (!activeCircle?.authorizationJWT || !activeCircle.recipient)
                return;

            const localTimestamp = getLocalDateTimeString();

            // Forward to DW Amplitude instance (best-effort, non-blocking)
            if (isDwAmplitudeEnabled) {
                trackDwEvent({
                    name: eventName,
                    properties: {
                        ...requestData,
                        [DW_EVENT_CHANNEL_KEY]: 'stream',
                        [DW_RECIPIENT_ID_KEY]: activeCircle.recipient.id,
                        user_id:
                            (requestData as DataWarehouseEventData).user_id ??
                            activeCircle.recipient?.userId ??
                            '',
                        local_timestamp: localTimestamp,
                    },
                }).catch(() => {});
            }

            // Skip backend call if streaming is disabled
            if (!dwStreamingEnabled) return;

            const rHGApp = getRhgAppHeader();
            const headerValues = getRhgAppHeaderObject();

            const api = createAnalyticsApi(getAccessTokenSilently);

            const response = await api.createAnalyticsStreamEvents({
                circleAuthorizationToken: activeCircle.authorizationJWT,
                recipientId: activeCircle.recipient.id,
                rHGApp,
                analyticsCreateAnalyticsStreamEventsRequest: {
                    events: [
                        {
                            eventName,
                            localTimestamp,
                            timestamp: getUtcDateTimeString(),
                            data: {
                                device_type: headerValues?.platform ?? '',
                                device_os: headerValues?.os ?? '',
                                device_model:
                                    headerValues?.['device-model'] ?? '',
                                app_version: headerValues?.version ?? '',
                                ...requestData,
                            },
                        },
                    ],
                },
            });
            return response;
        },
    });
    return {
        trackAnalyticsStreamEvent: mutate,
        trackAnalyticsStreamAsync: mutateAsync,
        ...otherProps,
    };
}
